import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { BadgeCheck, AlertTriangle, History, Loader2, Trash2, X } from "lucide-react";

const fmtStamp = (iso) => (iso ? new Date(iso).toLocaleString("en-US", {
  month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short",
}) : "");
const rangeLabel = (s) => `${s.start ? fmtDate(s.start) : "Beginning"} – ${s.end ? fmtDate(s.end) : "Present"}`;

export default function ReconSignOff({ accountId, range, summary, canSign, refreshKey }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (!accountId) return;
    try {
      const r = await api.get("/reconciliation/signoffs", { params: { bank_account_id: accountId } });
      setItems(r.data || []);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId, refreshKey]);

  const current = items.find((s) => (s.start || "") === (range.start || "") && (s.end || "") === (range.end || ""));

  const signOff = async () => {
    const issues = [];
    if (summary && summary.matched_count < summary.transaction_count)
      issues.push(`${summary.transaction_count - summary.matched_count} transaction(s) are not verified`);
    if (summary?.closing_difference != null && Math.abs(summary.closing_difference) >= 0.01)
      issues.push(`closing balances differ by ${fmtMoney(summary.closing_difference)}`);
    const msg = issues.length
      ? `This range is not fully reconciled:\n• ${issues.join("\n• ")}\n\nSign off anyway?`
      : `Sign off ${rangeLabel(range)}?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.post("/reconciliation/signoffs", {
        bank_account_id: accountId, start: range.start || null, end: range.end || null,
      });
      toast.success("Range signed off");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-off failed");
    } finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove the sign-off for ${rangeLabel(s)}? An audit record is kept.`)) return;
    try {
      await api.delete(`/reconciliation/signoffs/${s.id}`);
      toast.success("Sign-off removed");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div className="mb-5 flex flex-wrap items-center gap-3" data-testid="recon-signoff">
      {current ? (
        <SignOffBanner s={current} />
      ) : (
        <div className="text-xs text-[var(--ll-ink-soft)]" data-testid="recon-signoff-none">
          Not signed off for {rangeLabel(range)}
        </div>
      )}
      <div className="ml-auto flex items-center gap-2">
        <button
          onClick={() => setOpen(true)}
          data-testid="recon-signoff-history-open"
          className="text-xs px-3 py-2 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1.5 transition-colors"
          style={{ borderColor: "var(--ll-border)" }}
        >
          <History size={13} /> Sign-offs {items.length > 0 && <span className="font-mono-fin">({items.length})</span>}
        </button>
        {canSign && (
          <button
            onClick={signOff}
            disabled={busy || !accountId}
            data-testid="recon-signoff-btn"
            className="text-xs px-3 py-2 text-white bg-[var(--ll-ink)] hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-50 transition-opacity"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <BadgeCheck size={13} />}
            {current ? "Sign off again" : "Sign off range"}
          </button>
        )}
      </div>
      {open && <SignOffDrawer items={items} canSign={canSign} onRemove={remove} onClose={() => setOpen(false)} />}
    </div>
  );
}

function SignOffBanner({ s }) {
  return (
    <div
      className="flex items-center gap-2 text-sm px-3 py-2 border"
      style={{ background: s.changed_since ? "#FEF3C7" : "#DCFCE7", borderColor: s.changed_since ? "#F59E0B" : "#86EFAC" }}
      data-testid="recon-signoff-banner"
    >
      {s.changed_since ? <AlertTriangle size={15} className="text-[#B45309]" /> : <BadgeCheck size={15} className="text-[#166534]" />}
      <span>
        Signed off by <b data-testid="recon-signoff-by">{s.signed_by_name}</b> on {fmtStamp(s.signed_at)}
        {s.changed_since && (
          <b className="ml-2 text-[#B45309]" data-testid="recon-signoff-changed">· Changed since sign-off</b>
        )}
      </span>
    </div>
  );
}

function SignOffDrawer({ items, canSign, onRemove, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="recon-signoff-drawer">
      <div onClick={(e) => e.stopPropagation()} className="bg-white h-full w-full max-w-md shadow-xl flex flex-col">
        <div className="px-5 py-3 flex items-center justify-between bg-[var(--ll-ink)] text-white">
          <div className="font-display font-bold text-sm">Sign-off history</div>
          <button onClick={onClose} aria-label="Close" data-testid="recon-signoff-drawer-close"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="recon-signoff-empty">No sign-offs yet.</div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--ll-border)" }}>
              {items.map((s) => (
                <li key={s.id} className="p-4" data-testid={`recon-signoff-row-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 text-sm">
                      <div className="font-medium font-mono-fin">{rangeLabel(s)}</div>
                      <div className="text-xs text-[var(--ll-ink-soft)] mt-1">{s.signed_by_name} · {fmtStamp(s.signed_at)}</div>
                      <div className="text-xs mt-1 font-mono-fin">
                        {fmtMoney(s.opening_balance)} → {fmtMoney(s.closing_computed)} · {s.matched_count}/{s.transaction_count} verified
                      </div>
                      {s.changed_since && <div className="text-xs mt-1 text-[#B45309] font-medium">Changed since sign-off</div>}
                    </div>
                    {canSign && (
                      <button
                        onClick={() => onRemove(s)}
                        data-testid={`recon-signoff-remove-${s.id}`}
                        className="p-1.5 border hover:bg-[#FEE2E2] shrink-0"
                        style={{ borderColor: "var(--ll-border)" }}
                        title="Remove sign-off"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
